import { Injectable } from '@angular/core';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { WorkItemTypes } from '../../common/workitems/constants/workitem-types';
import { DiscussionOperatorTypes, DiscussionsMessagesTypes } from '../../shared/components/discussions/discussion-properties';
import { first } from 'rxjs/operators';
import { BcAuth } from 'common/auth/auth.service';
import { SubscriptionGroupModel } from '../../shared/services/workitem-notifications/subscription-group.model';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { EmailItem } from 'common/workitems/interfaces/email-item.interface';
import { WorkItem } from 'common/workitems/interfaces/workitem-base.interface';

@Injectable()
export class QueueViewNotificationsService {
  private discussionsObservable;

  private chatMessagesSubscriptions = {};
  private chatDiscussionMessagesSubscriptions = {};
  private textChatMessagesSubscription = {};
  private discussionMessagesSubscriptions = {};

  private chatSubscriptionGroup = new SubscriptionGroupModel();
  private emailsSubscriptionGroup = new SubscriptionGroupModel();
  private smsSubscriptionGroup = new SubscriptionGroupModel();
  private facebookSubscriptionGroup = new SubscriptionGroupModel();
  private asyncMessageSubscriptionGroup = new SubscriptionGroupModel();
  private abcSubscriptionGroup = new SubscriptionGroupModel();
  private discussionsSubscriptionGroup = new SubscriptionGroupModel();

  public forceDisableRedDots: boolean = true;
  private _operatorId = this._bcAuth.getSessionUserId();

  constructor(private _bcWorkItemListener: WorkItemListenerService,
              private _bcAuth: BcAuth) {
  }

  public initNotificationSubscriptions() {
    this.setupChatNotifications();
    this.setupEmailNotifications();
    this.setupTextChatNotifications();
    this.setupDiscussionsNotifications();
  }

  public clearAllSubscriptions() {
    if (this.chatSubscriptionGroup) {
      this._clearSubscriptionGroup(this.chatSubscriptionGroup);
    }
    if (this.emailsSubscriptionGroup) {
      this._clearSubscriptionGroup(this.emailsSubscriptionGroup);
    }
    if (this.smsSubscriptionGroup) {
      this._clearSubscriptionGroup(this.smsSubscriptionGroup);
    }
    if (this.facebookSubscriptionGroup) {
      this._clearSubscriptionGroup(this.facebookSubscriptionGroup);
    }
    if (this.asyncMessageSubscriptionGroup) {
      this._clearSubscriptionGroup(this.asyncMessageSubscriptionGroup);
    }
    if (this.abcSubscriptionGroup) {
      this._clearSubscriptionGroup(this.abcSubscriptionGroup);
    }
    if (this.discussionsSubscriptionGroup) {
      this._clearSubscriptionGroup(this.discussionsSubscriptionGroup);
    }

    if (this.chatMessagesSubscriptions) {
      this._clearSubscriptionsInGroup(this.chatMessagesSubscriptions);
    }
    if (this.chatDiscussionMessagesSubscriptions) {
      this._clearSubscriptionsInGroup(this.chatDiscussionMessagesSubscriptions);
    }
    if (this.textChatMessagesSubscription) {
      this._clearSubscriptionsInGroup(this.textChatMessagesSubscription);
    }
    if (this.discussionMessagesSubscriptions) {
      this._clearSubscriptionsInGroup(this.discussionMessagesSubscriptions);
    }
  }

  public setupChatNotifications(): void {
    let chatInitialFinished: boolean = false;

    this._bcWorkItemListener.createPromiseForInitialLoadingDone().then(() => {
      chatInitialFinished = true;
    });

    this.chatSubscriptionGroup.insert = this._bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.CHAT).subscribe((item) => {
      if (chatInitialFinished) {
        this.forceDisableRedDots = false;
      }
      this._createChatMessagesSubscriptionForChat(item);
      this._createDiscussionsMessageSubscriptionForChats(item);
    });

    this.chatSubscriptionGroup.remove = this._bcWorkItemListener.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT).subscribe((item: WorkItem & ChatItem) => {
      this._clearSingleSubscription(this.chatMessagesSubscriptions, item.ItemID);
      this._clearSingleSubscription(this.chatDiscussionMessagesSubscriptions, item.ItemID);
    });
  }

  public setupEmailNotifications(): void {
    let emailInitialFinished: boolean = false;

    this._bcWorkItemListener.createPromiseForInitialLoadingDone().then(() => {
      emailInitialFinished = true;
    });

    this.emailsSubscriptionGroup.insert = this._bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.EMAIL).subscribe(() => {
      if (emailInitialFinished) {
        this.forceDisableRedDots = false;
      }
    });

    this.emailsSubscriptionGroup.remove = this._bcWorkItemListener.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.EMAIL).subscribe((item: WorkItem & EmailItem) => {
      this._clearSingleSubscription(this.emailsSubscriptionGroup, item.ItemID);
    });
  }

  public setupTextChatNotifications(): void {
    this._setupTextChatMessagesSubscription(this.smsSubscriptionGroup, WorkItemTypes.SMS);
    this._setupTextChatMessagesSubscription(this.facebookSubscriptionGroup, WorkItemTypes.FACEBOOK);
    this._setupTextChatMessagesSubscription(this.asyncMessageSubscriptionGroup, WorkItemTypes.ASYNC_MESSAGE);
  }

  public setupDiscussionsNotifications(): void {
    let discussionInitialFinished: boolean = false;
    this.discussionsObservable = this._bcWorkItemListener.observeActiveDiscussionsByLoginId();
    let initDonePromise = this.discussionsObservable.done().pipe(first()).toPromise();
    initDonePromise.then(() => {
      discussionInitialFinished = true;
    });

    this.discussionsSubscriptionGroup.insert = this.discussionsObservable.inserted().subscribe((item) => {
      if (discussionInitialFinished) {
        this.forceDisableRedDots = false;
      }
      this._createDiscussionsMessageSubscription(item);
    });

    this.discussionsSubscriptionGroup.remove = this.discussionsObservable.deleted().subscribe((item) => {
      this._clearSingleSubscription(this.discussionMessagesSubscriptions, item.WorkItemID);
    });
  }

  private _createChatMessagesSubscriptionForChat(chatItem): void {
    let messagesInitialFinished: boolean = false;
    const chatMessagesObservable = this._bcWorkItemListener.createObservableForChatMessagesByChatId(chatItem.ItemID);
    let initDonePromise = chatMessagesObservable.done().pipe(first()).toPromise();
    initDonePromise.then(() => {
      messagesInitialFinished = true;
    });

    this.chatMessagesSubscriptions[chatItem.ItemID] = chatMessagesObservable.inserted().subscribe((workItemMessage) => {
      if (messagesInitialFinished && workItemMessage.Name !== null && workItemMessage.PersonID !== this._operatorId) {
        this.forceDisableRedDots = false;
      }
    });
  }

  private _createDiscussionsMessageSubscriptionForChats(chatItem): void {
    let chatDiscussionMessagesInitialFinished: boolean = false;
    const chatDiscussionMessagesObservable = this._bcWorkItemListener.createObservableForDiscussionsMessagesByChatId(chatItem.ItemID);
    let initDonePromise = chatDiscussionMessagesObservable.done().pipe(first()).toPromise();
    initDonePromise.then(() => {
      chatDiscussionMessagesInitialFinished = true;
    });

    this.chatDiscussionMessagesSubscriptions[chatItem.ItemID] = chatDiscussionMessagesObservable.inserted().subscribe((discussionsMessage) => {
      if (chatDiscussionMessagesInitialFinished && discussionsMessage.MessageType === DiscussionsMessagesTypes.OPERATOR && discussionsMessage.OperatorType === DiscussionOperatorTypes.HELPER) {
        this.forceDisableRedDots = false;
      }
    });
  }

  private _setupTextChatMessagesSubscription(subscriptionGroup, workItemType: WorkItemTypes): void {
    let initialFinished: boolean = false;

    this._bcWorkItemListener.createPromiseForInitialLoadingDone().then(() => {
      initialFinished = true;
    });

    subscriptionGroup.insert = this._bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(workItemType).subscribe((item) => {
      this._createTextChatMessagesSubscription(item);
      if (initialFinished) {
        this.forceDisableRedDots = false;
      }
    });

    subscriptionGroup.remove = this._bcWorkItemListener.createAcdBasedObservableForRemoveByWorkItemType(workItemType).subscribe((item: WorkItem & ChatItem) => {
      this._clearSingleSubscription(this.textChatMessagesSubscription, item.ItemID);
    });
  }

  private _createTextChatMessagesSubscription(textChatWorkItem): void {
    let textChatMessagesInitialFinished: boolean = false;

    const textChatMessagesObservable = this._bcWorkItemListener.createObservableForTextChatMessages(textChatWorkItem);
    let initDonePromise = textChatMessagesObservable.done().pipe(first()).toPromise();
    initDonePromise.then(() => {
      textChatMessagesInitialFinished = true;
    });
    this.textChatMessagesSubscription[textChatWorkItem.WorkItemID] = textChatMessagesObservable.inserted().subscribe(() => {
      if (textChatMessagesInitialFinished) {
        this.forceDisableRedDots = false;
      }
    });
  }

  private _createDiscussionsMessageSubscription(discussion): void {
    let discussionMessagesInitialFinished: boolean = false;
    const discussionMessagesObservable = this._bcWorkItemListener.createObservableForDiscussionsMessagesByChatId(discussion.WorkItemID);
    let initDonePromise = discussionMessagesObservable.done().pipe(first()).toPromise();
    initDonePromise.then(() => {
      discussionMessagesInitialFinished = true;
    });

    this.discussionMessagesSubscriptions[discussion.WorkItemID] = discussionMessagesObservable.inserted().subscribe((messageItem) => {
      if (discussionMessagesInitialFinished && messageItem.MessageType === DiscussionsMessagesTypes.OPERATOR && messageItem.OperatorType === DiscussionOperatorTypes.OPERATOR) {
        this.forceDisableRedDots = false;
      }
    });
  }

  private _clearSubscriptionGroup(subscriptionGroup) {
    if (subscriptionGroup.insert) {
      subscriptionGroup.insert.unsubscribe();
      subscriptionGroup.insert = null;
    }
    if (subscriptionGroup.remove) {
      subscriptionGroup.remove.unsubscribe();
      subscriptionGroup.remove = null;
    }
  }

  private _clearSingleSubscription(subscriptionsList, workItemId) {
    if (subscriptionsList[workItemId]) {
      subscriptionsList[workItemId].unsubscribe();
      subscriptionsList[workItemId] = null;
    }
  }

  private _clearSubscriptionsInGroup(subscription) {
    for (let key of Object.keys(subscription)) {
      if (subscription[key]) {
        subscription[key].unsubscribe();
        subscription[key] = null;
      }
    }
  }
}
